// D-177.7 (s177, A2) — the ESSENTIALS SPEED BUMP for "This doesn't apply to me".
//
// After the suggestion-row retirement (D-177.4) a shaded section can legitimately be empty, and an
// empty section is not done (D-177.5b). "This doesn't apply to me" is the one honest way to finish
// one — which means the affordance now reaches ESSENTIAL sections, the facts an executor most needs.
//
// Reade's guardrail: on an essential, the action confirms ONCE, in plain language, saying what the
// planner is actually telling their executor. Honest answers are allowed — a renter really has no
// property — so this is a speed bump, never a wall: one sentence, one tap, no typing, no second
// confirm, and it does not appear at all on non-essential sections.
//
// Deliberately NOT a generic "Are you sure?". The whole value is naming the CONSEQUENCE in the
// planner's own terms ("you have no bank accounts"), so a mis-tap reads as obviously wrong. Copy per
// section lives in ESSENTIAL_NA_CONFIRM below rather than being assembled from the section name,
// because "you have no Funeral Wishes" is not a sentence anybody says.
//
// G7: built on the same shadcn AlertDialog primitive as GateClearConfirmDialog / DeleteConfirmDialog
// rather than a fourth bespoke modal.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SECTION_SLUGS } from "@shared/constants/sections";

/**
 * The consequence sentence, per ESSENTIAL section (D-177.7). A section absent from this map gets
 * NO confirm — that absence is the non-essential path, not an oversight, and `needsNaConfirm`
 * below is the single place that distinction is made.
 *
 * Phrased as what the EXECUTOR will be told, because that is the fact the planner is asserting.
 * Second person, no jargon, no "N/A".
 */
export const ESSENTIAL_NA_CONFIRM: Record<string, string> = {
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]:
    "This tells your executor you have no bank accounts. Is that right?",
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]:
    "This tells your executor you have no insurance policies. Is that right?",
  [SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS]:
    "This tells your executor there is no lawyer, accountant or advisor to contact. Is that right?",
  [SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS]:
    "This tells your executor there is no will or other key document to find. Is that right?",
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]:
    "This tells your executor you have left no funeral wishes. Is that right?",
  [SECTION_SLUGS.ASSETS_PROPERTIES]:
    "This tells your executor you own no property. Is that right?",
  [SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP]:
    "This tells your executor you have no investments or registered accounts. Is that right?",
  [SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES]:
    "This tells your executor you have no debts or liabilities. Is that right?",
  [SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS]:
    "This tells your executor you have no pension or other income to claim. Is that right?",
};

/** True iff marking this section "doesn't apply" should confirm first (D-177.7: essentials only). */
export function needsNaConfirm(sectionSlug: string): boolean {
  return sectionSlug in ESSENTIAL_NA_CONFIRM;
}

interface Props {
  /** The section awaiting confirmation, or null when the dialog is closed. */
  sectionSlug: string | null;
  sectionName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SectionNaConfirmDialog({ sectionSlug, sectionName, onConfirm, onCancel }: Props) {
  const message = sectionSlug ? ESSENTIAL_NA_CONFIRM[sectionSlug] : undefined;
  return (
    <AlertDialog open={!!sectionSlug && !!message} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent data-testid="section-na-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {sectionName ? `Mark ${sectionName} as not applying?` : "Mark this section as not applying?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {message}
            {" "}You can bring it back any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} data-testid="section-na-confirm-cancel">
            Go back
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="section-na-confirm-accept">
            Yes, it doesn't apply
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
